import '../../CommonStyles.scss';
import '../TablesCommon.scss';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@trackman/web-shared-components';
import { useGetOrganizationsSummary } from 'queryHooks/Admin/analytics';
import { useState } from 'react';
import { sortByDate, sortByString } from 'utilities/dataSorters';
import { SourceBadge } from '../SourceBadge/SourceBadge';

type OrderBy = 'organization' | 'classes' | 'students' | 'since';
type OrderDirection = 'ASCENDING' | 'DESCENDING';

export const OrganizationsBreakdown = () => {
  const DEFAULT_ORDER_BY: OrderBy = 'students';
  const DEFAULT_ORDER_DIRECTION: OrderDirection = 'DESCENDING';
  const [orderBy, setOrderBy] = useState<OrderBy>(DEFAULT_ORDER_BY);
  const [orderDirection, setOrderDirection] = useState<OrderDirection>(DEFAULT_ORDER_DIRECTION);

  const handleResetSorting = () => {
    setOrderBy(DEFAULT_ORDER_BY);
    setOrderDirection(DEFAULT_ORDER_DIRECTION);
  };

  return (
    <Paper className='analytics-card analytics-table'>
      <div className='analytics-table__header'>
        <div className='analytics-card__title-group'>
          <span className='card-title'>Organizations Breakdown</span>
          <SourceBadge source='DB' />
        </div>

        {orderBy !== DEFAULT_ORDER_BY || orderDirection !== DEFAULT_ORDER_DIRECTION ? (
          <span className='reset-sorting' onClick={handleResetSorting}>
            Reset
          </span>
        ) : null}
      </div>

      <OrganizationsBreakdownTable
        orderBy={orderBy}
        orderDirection={orderDirection}
        setOrderBy={setOrderBy}
        setOrderDirection={setOrderDirection}
      />
    </Paper>
  );
};

const OrganizationsBreakdownTable = ({
  orderBy,
  orderDirection,
  setOrderBy,
  setOrderDirection,
}: {
  orderBy: OrderBy;
  orderDirection: OrderDirection;
  setOrderBy: (orderBy: OrderBy) => void;
  setOrderDirection: (orderDirection: OrderDirection) => void;
}) => {
  const { data: organizations } = useGetOrganizationsSummary();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const tableHeadCells: { id: OrderBy; label: string }[] = [
    { id: 'organization', label: 'Organization' },
    { id: 'classes', label: 'Classes' },
    { id: 'students', label: 'Students' },
    { id: 'since', label: 'Since' },
  ];

  const tableData = () => {
    if (!organizations) {
      return [];
    }
    if (orderBy === 'organization') {
      return sortByString(organizations, 'name', orderDirection);
    } else if (orderBy === 'since') {
      return sortByDate(organizations, (org) => org.createdAt, orderDirection);
    } else if (orderBy === 'classes') {
      return organizations.sort((a, b) => {
        if (orderDirection === 'ASCENDING') {
          return a.classesCount - b.classesCount;
        } else {
          return b.classesCount - a.classesCount;
        }
      });
    } else if (orderBy === 'students') {
      return organizations.sort((a, b) => {
        if (orderDirection === 'ASCENDING') {
          return a.studentsCount - b.studentsCount;
        } else {
          return b.studentsCount - a.studentsCount;
        }
      });
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSorting = (column: 'organization' | 'classes' | 'students' | 'since') => {
    if (orderBy !== column) {
      setOrderBy(column);
    }
    setOrderDirection(orderDirection === 'ASCENDING' ? 'DESCENDING' : 'ASCENDING');
  };

  return (
    <Table className='tmu-table'>
      <TableHead>
        <TableRow>
          {tableHeadCells.map((cell) => {
            return (
              <TableCell>
                <TableSortLabel direction={orderDirection === 'ASCENDING' ? 'asc' : 'desc'} onClick={() => handleSorting(cell.id)}>
                  {cell.label}
                </TableSortLabel>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {tableData()
          ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((row, index) => (
            <TableRow key={index}>
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.classesCount}</TableCell>
              <TableCell>{row.studentsCount}</TableCell>
              <TableCell>{new Date(row.createdAt).toLocaleDateString()}</TableCell>
            </TableRow>
          ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, { label: 'All', value: -1 }]}
            labelRowsPerPage='Rows per page'
            count={tableData()?.length ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableRow>
      </TableFooter>
    </Table>
  );
};
